// BookingCard — the Ready state. Renders the `.card` + title + a stack of
// `.visit` rows + one shared submit button that the veteran taps once every
// row has an answer.
//
// One token surfaces all of the contact's in-window unconfirmed bookings
// (see resolveToken on the server). The submit button POSTs each answered
// row separately; the hook owns the per-booking submission map and the
// terminal-state transitions when one row 409s mid-flight.

import { useMemo, useState } from 'react';
import type {
  BookingView,
  ConfirmAction,
  DidNotAttendDetail,
  DidNotAttendReason,
} from '../types';
import { CheckIcon, CrossIcon } from './Icons';
import { NoReasonFlow } from './NoReasonFlow';

export interface BookingCardProps {
  bookings: BookingView[];
  submitting: boolean;
  /** Status keyed by booking id — once a row's submission completes the
   *  hook adds it here so the row renders in a terminal/disabled state. */
  submittedActions: Record<string, ConfirmAction>;
  /** POST one answer. The card calls this for each unanswered booking when
   *  the user taps the submit pill. For a `did_not_attend` answer it passes
   *  the captured `reason` (+ optional per-reason `detail`); a `confirmed`
   *  answer passes neither. Returns true on success — false means the hook
   *  has moved to a terminal state (409/410/5xx) and the bundle loop should
   *  stop firing further POSTs. */
  onSubmit: (
    bookingId: string,
    action: ConfirmAction,
    reason?: DidNotAttendReason | null,
    detail?: DidNotAttendDetail | null,
  ) => Promise<boolean> | boolean;
}

/** Captured reason + detail for a "no" row. A row that has answered "no" is
 *  not considered complete until an entry lands here (see `unanswered`). */
type ReasonEntry = { reason: DidNotAttendReason; detail: DidNotAttendDetail | null };

type Answer = 'yes' | 'no' | null;

/** Returns a "Monday 18 May 2026" style label for a per-visit row. */
function formatDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** Derives a 10:00 AM – 11:30 AM style range from ISO start/end strings. */
function formatTimeRange(startIso: string, endIso: string | null): string {
  const start = new Date(startIso);
  const fmt = (d: Date) =>
    d
      .toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: true })
      // Normalise "10:00 am" → "10:00 AM"
      .replace(/\s?am/i, ' AM')
      .replace(/\s?pm/i, ' PM');
  const s = fmt(start);
  if (!endIso) return s;
  const end = new Date(endIso);
  return `${s} – ${fmt(end)}`;
}

const visitDateStyle: React.CSSProperties = {
  fontSize: '12.5px',
  color: 'var(--sage-deep)',
  letterSpacing: '.12em',
  textTransform: 'uppercase',
  fontWeight: 700,
  marginBottom: '6px',
};

export function BookingCard({ bookings, submitting, submittedActions, onSubmit }: BookingCardProps) {
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  // Per-booking captured reason for "no" answers. A "no" row only counts as
  // answered once its reason flow has completed and an entry appears here.
  const [reasons, setReasons] = useState<Record<string, ReasonEntry>>({});

  // Pick a "yes"/"no" answer. Flipping a row's answer always clears any reason
  // captured for it — a stale reason must never ride a re-tapped "yes", and a
  // re-tapped "no" restarts the reason flow from scratch.
  const setAnswer = (id: string, value: Answer) => {
    setAnswers((s) => ({ ...s, [id]: value }));
    setReasons((s) => {
      if (s[id] === undefined) return s;
      const next = { ...s };
      delete next[id];
      return next;
    });
  };

  const unanswered = useMemo(
    () =>
      bookings.filter((b) => {
        if (submittedActions[b.id] !== undefined) return false; // already settled
        const ans = answers[b.id] ?? null;
        if (ans === null) return true; // no yes/no yet
        // A "no" row needs its reason captured before it counts as answered.
        if (ans === 'no') return reasons[b.id] === undefined;
        return false; // "yes" is complete on its own
      }).length,
    [bookings, answers, reasons, submittedActions],
  );
  const allAnswered = unanswered === 0;

  const handleSubmit = async () => {
    if (!allAnswered || submitting) return;
    for (const b of bookings) {
      // Skip rows the hook has already recorded as submitted (e.g. partial
      // submit retry after a transient failure).
      if (submittedActions[b.id] !== undefined) continue;
      const ans = answers[b.id];
      if (!ans) continue;
      let ok: boolean;
      if (ans === 'yes') {
        ok = await onSubmit(b.id, 'confirmed');
      } else {
        // A "no" row is only allowed past the gate once its reason is captured.
        const entry = reasons[b.id];
        ok = await onSubmit(b.id, 'did_not_attend', entry?.reason ?? null, entry?.detail ?? null);
      }
      // If a POST returned a terminal error (e.g. 409 CANCELLED / 410
      // EXPIRED / 5xx), the hook has flipped state and the card is about
      // to unmount — stop firing further redundant POSTs for the rest of
      // the bundle.
      if (!ok) break;
    }
  };

  const totalUnsubmitted = bookings.filter(
    (b) => submittedActions[b.id] === undefined,
  ).length;
  let buttonLabel: string;
  if (submitting) buttonLabel = 'Sending…';
  else if (allAnswered) buttonLabel = totalUnsubmitted > 1 ? 'Send my answers' : 'Send my answer';
  else if (unanswered === totalUnsubmitted)
    buttonLabel = totalUnsubmitted === 1 ? 'Answer to continue' : `Answer all ${totalUnsubmitted} to continue`;
  else buttonLabel = `Answer ${unanswered} more to continue`;

  const visitsLeadLabel = bookings.length === 1 ? 'Your visit' : 'Your visits';

  return (
    <div className="card" style={{ position: 'relative' }}>
      <div className="greet">
        <h1>Service Confirmation</h1>
      </div>

      <div className="visits">
        <p className="visits-lead">{visitsLeadLabel}</p>

        {bookings.map((b) => {
          const ans = answers[b.id] ?? null;
          const time = formatTimeRange(b.start_time, b.end_time);
          const dateLabel = formatDateLabel(b.start_time);
          const settled = submittedActions[b.id] !== undefined;
          return (
            <div className="visit" key={b.id}>
              <div style={visitDateStyle}>{dateLabel}</div>
              <div className="row1">
                <span className="svc">{b.service_type}</span>
                <span className="time">{time}</span>
              </div>
              {b.provider_name && <div className="with">with {b.provider_name}</div>}

              <p className="ask">Was your service completed?</p>

              <div
                className="yn"
                role="radiogroup"
                aria-label={`${b.service_type} on ${dateLabel} — was it completed?`}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={ans === 'yes'}
                  className={`yes${ans === 'yes' ? ' picked' : ''}`}
                  onClick={() => setAnswer(b.id, 'yes')}
                  disabled={submitting || settled}
                >
                  <CheckIcon />
                  <span>Yes, it happened</span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={ans === 'no'}
                  className={`no${ans === 'no' ? ' picked' : ''}`}
                  onClick={() => setAnswer(b.id, 'no')}
                  disabled={submitting || settled}
                >
                  <CrossIcon />
                  <span>No, it didn't</span>
                </button>
              </div>

              {ans === 'no' && !settled && (
                // Replaces the old static "we'll call you" note: capture *why*
                // it didn't happen. The answer isn't complete (and submit
                // stays gated) until onComplete stashes a reason for this row.
                <NoReasonFlow
                  bookingId={b.id}
                  disabled={submitting}
                  onComplete={(reason, detail) =>
                    setReasons((s) => ({ ...s, [b.id]: { reason, detail } }))
                  }
                  onReset={() => setAnswer(b.id, null)}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="submit-row">
        <button
          className="submit-btn"
          onClick={handleSubmit}
          disabled={!allAnswered || submitting}
        >
          {buttonLabel}
        </button>
        <div className="status" aria-live="polite"></div>
      </div>

      {submitting && (
        <div className="submitting" role="status" aria-live="polite">
          <div className="spinner" aria-hidden="true"></div>
          <div className="lbl">Sending</div>
        </div>
      )}
    </div>
  );
}
